import { useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button, NumberInput,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, Select, TagsInput, Image,
  SimpleGrid, Tooltip,
} from '@mantine/core'
import { IconPlus, IconPencil, IconTrash, IconPhoto, IconEye, IconEyeOff, IconStar, IconStarFilled, IconX } from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete } from '@/lib/confirm'
import { useRooms } from '@/lib/swr'
import type { Room } from '@/types'

interface RoomFormData {
  name: string
  type: Room['type']
  description: string
  price: number
  prepaymentAmount: number
  capacity: number
  bedWidth: string
  area: number
  amenities: string[]
  images: string[]
  image: string
  totalUnits: number
}

const EMPTY_FORM: RoomFormData = {
  name: '', type: 'standard',
  description: '', price: 0, prepaymentAmount: 0, capacity: 1,
  bedWidth: '', area: 0,
  amenities: [], images: [], image: '', totalUnits: 1,
}

export default function RoomList() {
  const { data, isLoading, mutate } = useRooms({ limit: '100' })
  const rooms: Room[] = data?.data ?? []

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RoomFormData>({ defaultValues: EMPTY_FORM })

  const watchImages = watch('images')
  const watchMainImage = watch('image')

  const openCreate = () => {
    setEditId(null); reset(EMPTY_FORM); setModalOpen(true)
  }

  const openEdit = (r: Room) => {
    setEditId(r._id)
    reset({
      name: r.name, type: r.type,
      description: r.description, price: r.price,
      prepaymentAmount: r.prepaymentAmount, capacity: r.capacity,
      bedWidth: r.bedWidth || '', area: r.area || 0,
      amenities: r.amenities || [], images: r.images || [],
      image: r.image || '', totalUnits: r.totalUnits,
    })
    setModalOpen(true)
  }

  const closeModal = () => { setModalOpen(false); reset(EMPTY_FORM) }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        prepaymentAmount: Number(form.prepaymentAmount),
        capacity: Number(form.capacity),
        area: Number(form.area),
        totalUnits: Number(form.totalUnits),
      }
      if (editId) await api.admin.updateRoom(editId, payload)
      else await api.admin.createRoom(payload)
      toast.success(editId ? 'Шинэчлэгдлээ' : 'Нэмэгдлээ')
      closeModal(); mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const remove = (id: string) => confirmDelete('Энэ өрөөг устгах уу?', async () => {
    try {
      await api.admin.deleteRoom(id)
      toast.success('Устгагдлаа')
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const toggleStatus = async (r: Room) => {
    try {
      await api.admin.updateRoomStatus(r._id, !r.isActive); mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  }

  const handleMultiImageUpload = async (files: File[]) => {
    setUploading(true)
    const uploadedUrls: string[] = []
    for (const file of files) {
      try {
        const result = await api.admin.uploadImage(file)
        uploadedUrls.push(result.url)
      } catch {
        toast.error(`"${file.name}" байршуулахад алдаа гарлаа`)
      }
    }
    if (uploadedUrls.length > 0) {
      const currentImages = getValues('images') || []
      const currentMain = getValues('image')
      setValue('images', [...currentImages, ...uploadedUrls], { shouldDirty: true })
      if (!currentMain) {
        setValue('image', uploadedUrls[0], { shouldDirty: true })
      }
    }
    setUploading(false)
  }

  const removeImage = (url: string) => {
    const current = getValues('images') || []
    const updated = current.filter((img) => img !== url)
    setValue('images', updated, { shouldDirty: true })
    if (getValues('image') === url) {
      setValue('image', updated[0] || '', { shouldDirty: true })
    }
  }

  const setMainImage = (url: string) => {
    setValue('image', url, { shouldDirty: true })
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Өрөөнүүд</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Өрөө нэмэх</Button>
      </Group>

      <Card withBorder pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Зураг</Table.Th>
              <Table.Th>Нэр</Table.Th>
              <Table.Th>Төрөл</Table.Th>
              <Table.Th>Үнэ</Table.Th>
              <Table.Th>Урьдчилгаа</Table.Th>
              <Table.Th>Хүчин чадал</Table.Th>
              <Table.Th>Орны өргөн</Table.Th>
              <Table.Th>Талбай</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th w={120}>Үйлдэл</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rooms.map((r) => (
              <Table.Tr key={r._id}>
                <Table.Td>
                  {r.image ? (
                    <img src={r.image} alt="" style={{ width: 48, height: 36, borderRadius: 4, objectFit: 'cover' }} />
                  ) : <Text c="dimmed" size="xs">—</Text>}
                </Table.Td>
                <Table.Td><Text size="sm" fw={600}>{r.name}</Text></Table.Td>
                <Table.Td><Badge variant="light" size="sm">{r.type}</Badge></Table.Td>
                <Table.Td><Text size="sm">{r.price.toLocaleString()}₮</Text></Table.Td>
                <Table.Td><Text size="sm">{r.prepaymentAmount.toLocaleString()}₮</Text></Table.Td>
                <Table.Td><Text size="sm">{r.capacity} хүн</Text></Table.Td>
                <Table.Td><Text size="sm">{r.bedWidth || '—'}</Text></Table.Td>
                <Table.Td><Text size="sm">{r.area ? `${r.area} м²` : '—'}</Text></Table.Td>
                <Table.Td>
                  <Badge color={r.isActive ? 'green' : 'gray'} variant="light" size="sm">
                    {r.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon variant="light" size="sm" onClick={() => toggleStatus(r)}>
                      {r.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                    </ActionIcon>
                    <ActionIcon variant="light" size="sm" onClick={() => openEdit(r)}>
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon variant="light" color="red" size="sm" onClick={() => remove(r._id)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {rooms.length === 0 && !isLoading && (
              <Table.Tr><Table.Td colSpan={10}><Text ta="center" c="dimmed" py="lg">Өрөө бүртгэгдээгүй байна</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={modalOpen} onClose={closeModal} title={editId ? 'Өрөө засах' : 'Шинэ өрөө'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="name"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Нэр шаардлагатай') }}
              render={({ field }) => <TextInput label="Нэр *" {...field} error={errors.name?.message} />}
            />
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select
                  label="Төрөл"
                  data={[
                    { value: 'standard', label: 'Стандарт' },
                    { value: 'vip', label: 'VIP' },
                    { value: 'luxury', label: 'Люкс' },
                    { value: 'other', label: 'Бусад' },
                  ]}
                  value={field.value}
                  onChange={(v) => field.onChange((v as Room['type']) || 'standard')}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={3} />}
            />
            <Group grow>
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Үнэ (₮)" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} thousandSeparator="," />
                )}
              />
              <Controller
                name="prepaymentAmount"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Урьдчилгаа төлбөр (₮)" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} thousandSeparator="," />
                )}
              />
            </Group>
            <Group grow>
              <Controller
                name="capacity"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Хүчин чадал (хүн)" value={field.value} onChange={(v) => field.onChange(Number(v) || 1)} min={1} />
                )}
              />
              <Controller
                name="totalUnits"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Нийт ширхэг" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} />
                )}
              />
            </Group>
            <Group grow>
              <Controller
                name="bedWidth"
                control={control}
                render={({ field }) => (
                  <TextInput label="Орны өргөн" placeholder="Жишээ: 120cm, 160cm" {...field} />
                )}
              />
              <Controller
                name="area"
                control={control}
                render={({ field }) => (
                  <NumberInput label="Өрөөний талбай (м²)" value={field.value} onChange={(v) => field.onChange(Number(v) || 0)} min={0} decimalScale={1} />
                )}
              />
            </Group>
            <Controller
              name="amenities"
              control={control}
              render={({ field }) => (
                <TagsInput
                  label="Тоног төхөөрөмж / amenities"
                  placeholder="Жишээ: WiFi"
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                />
              )}
            />

            {/* Multiple images with main image selection */}
            <div>
              <Group justify="space-between" mb={4}>
                <Text size="sm" fw={500}>Зурагнууд</Text>
                <Button
                  size="xs" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'; input.accept = 'image/*'; input.multiple = true
                    input.onchange = () => {
                      if (!input.files || input.files.length === 0) return
                      handleMultiImageUpload(Array.from(input.files))
                    }
                    input.click()
                  }}
                >Зураг нэмэх</Button>
              </Group>

              {watchImages && watchImages.length > 0 ? (
                <SimpleGrid cols={4} spacing="xs">
                  {watchImages.map((url) => (
                    <div key={url} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: watchMainImage === url ? '2px solid var(--mantine-color-blue-5)' : '2px solid transparent' }}>
                      <Image src={url} alt="" h={80} fit="cover" radius="sm" />
                      <Group gap={2} style={{ position: 'absolute', top: 4, right: 4 }}>
                        <Tooltip label={watchMainImage === url ? 'Үндсэн зураг' : 'Үндсэн болгох'}>
                          <ActionIcon size="xs" variant="filled" color={watchMainImage === url ? 'yellow' : 'gray'} onClick={() => setMainImage(url)}>
                            {watchMainImage === url ? <IconStarFilled size={10} /> : <IconStar size={10} />}
                          </ActionIcon>
                        </Tooltip>
                        <ActionIcon size="xs" variant="filled" color="red" onClick={() => removeImage(url)}>
                          <IconX size={10} />
                        </ActionIcon>
                      </Group>
                    </div>
                  ))}
                </SimpleGrid>
              ) : (
                <Text size="xs" c="dimmed">Зураг байхгүй байна</Text>
              )}
            </div>

            <Group justify="flex-end">
              <Button variant="light" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
